import React, { useState, useEffect, useCallback } from "react";

function CampaignStepsTutorial({ onClose }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [highlightRect, setHighlightRect] = useState(null);
  const [tooltipPos, setTooltipPos] = useState({
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    placement: "center",
    position: "fixed",
  });

  const steps = [
    // 0 — Intro
    {
      id: "intro",
      title: "Welcome to the Campaign Editor",
      description:
        "This is where you design your campaign: rename it, build the email sequence, and see contacts & replies.",
      position: "center",
      isIntro: true,
    },

    // 1 — Settings tab button
    {
      id: "settings-tab",
      title: "Go to Settings",
      description:
        "Click the Settings tab first. There you can rename the campaign so it clearly describes what you’re sending.",
      selectors: ["[data-tour='tab-settings']"],
      position: "bottom",
    },

    // 2 — Settings card (rename)
    {
      id: "settings-card",
      title: "Rename your campaign",
      description:
        "In Settings, you can rename the campaign (for example “EDM curators – new single”). This name is just for you.",
      selectors: [".psl-main section:nth-of-type(1) .psl-camp-card"],
      position: "bottom",
    },

    // 3 — Editor tab button
    {
      id: "editor-tab",
      title: "Switch to the Editor",
      description:
        "Now click the Editor tab. That’s where you build the sequence of emails and follow-ups.",
      selectors: ["[data-tour='tab-editor']"],
      position: "bottom",
    },

    // 4 — Editor card / sequence board
    {
      id: "editor-board",
      title: "Sequence board",
      description:
        "The Editor shows your sequence. Each step is one email in the thread.",
      selectors: [".psl-main section:nth-of-type(2) .psl-camp-card"],
      position: "top",
    },

    // 5 — Add Step
    {
      id: "add-step",
      title: "Add your first step",
      description:
        'Click “Add Step” to create your first email step in this campaign.',
      selectors: ["[data-tour='add-step']"],
      position: "right",
    },

    // 6 — When this step sends
    {
      id: "when-link",
      title: "When this step sends",
      description:
        "Use this control to decide when this step sends: immediately, after a delay, or at a specific hour.",
      selectors: [".sb-when-link"],
      position: "bottom",
    },

    // 7 — Timing popover
    {
      id: "schedule-pop",
      title: "Set the timing & save so it closes",
      description:
        "Pick when you want this step to send, then click Save in this popover so it closes. Once it’s closed, you can continue to the next step.",
      selectors: [".psl-pop.sb-pop"],
      position: "bottom",
    },

    // 8 — Expand first step
    {
      id: "open-step",
      title: "Open the step editor",
      description:
        "Click this expand icon to open the email editor for this step.",
      selectors: [".psl-row-toggle"],
      position: "bottom",
      autoScroll: true,
    },

    // 9 — Editor pane
    {
      id: "editor-pane",
      title: "Write your playlist pitch",
      description:
        "Here you write the email you’d send to get your track onto a playlist. Add a short intro and link to your release.",
      selectors: [".psl-editor-pane.sb-pane"],
      position: "top",
    },

    // 10 — Dynamic variables
    {
      id: "dynvars",
      title: "Use dynamic variables",
      description:
        "These variables pull info from your added playlists: curator name, playlist name, playlist link and your name. Each contact gets a personalized message.",
      selectors: [".psl-tokenbar.sb-tokenbar"],
      position: "bottom",
    },

    // 11 — Save Sequence
    {
      id: "save-sequence",
      title: "Save your sequence",
      description:
        'When you’re happy with your steps, click “Save Sequence” to save all changes to this campaign.',
      selectors: ["[data-tour='save-sequence']"],
      position: "bottom",
      autoScroll: true,
    },

    // 12 — Contacts tab button
    {
      id: "contacts-tab",
      title: "Open Contacts",
      description:
        "Click the Contacts tab to see everyone you’ve added to this campaign.",
      selectors: ["[data-tour='tab-contacts']"],
      position: "bottom",
      autoScroll: true,
    },

    // 13 — Contacts card
    {
      id: "contacts-card",
      title: "See who you contacted",
      description:
        "Here you’ll see which curators were queued, emailed, bounced or replied. Click a row to view their activity timeline.",
      selectors: [".psl-main section:nth-of-type(3) .psl-camp-card"],
      position: "bottom",
      autoScroll: true,
    },

    // 14 — Search → Revealed
    {
      id: "search-revealed",
      title: "Next: add contacts from Search",
      description:
        "When you’re ready, go to the Search page, open the Revealed section and add curators into this campaign using the Add to campaign / revealed button.",
      selectors: [
        "a.nav-link[href='/search']",
        ".mobile-links a[href='/search']",
      ],
      position: "bottom",
    },
  ];

  const handleClose = useCallback(
    (completed = false) => {
      if (completed) {
        try {
          localStorage.setItem("campaignStepsTutorialCompleted", "true");
        } catch {
          // ignore if storage is unavailable
        }
      }
      onClose && onClose();
    },
    [onClose]
  );

  // --- POSITIONING & HIGHLIGHT LOGIC (same pattern as SearchTutorial) ---
  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined") {
      return;
    }
    if (currentStep >= steps.length) return;

    const step = steps[currentStep];

    const centerTooltip = () => {
      setHighlightRect(null);
      setTooltipPos({
        top: "50%",
        left: "50%",
        transform: "translate(-50%, -50%)",
        placement: "center",
        position: "fixed",
      });
    };

    const calculateTooltipPos = (rect, preferredPosition) => {
      if (!rect || preferredPosition === "center" || !preferredPosition) {
        centerTooltip();
        return;
      }

      const gap = 16;
      const tooltipW = 320;
      let top = 0;
      let left = 0;

      if (preferredPosition === "top") {
        top = rect.top - gap;
        left = rect.left + rect.width / 2 - tooltipW / 2;
      } else if (preferredPosition === "bottom") {
        top = rect.top + rect.height + gap;
        left = rect.left + rect.width / 2 - tooltipW / 2;
      } else if (preferredPosition === "left") {
        top = rect.top;
        left = rect.left - gap - tooltipW;
      } else if (preferredPosition === "right") {
        top = rect.top;
        left = rect.left + rect.width + gap;
      } else {
        centerTooltip();
        return;
      }

      if (left < 10) left = 10;
      const maxLeft = window.innerWidth - tooltipW - 10;
      if (left > maxLeft) left = maxLeft;

      let transform = "none";
      if (preferredPosition === "top") {
        transform = "translateY(-100%)";
      }

      setTooltipPos({
        top,
        left,
        transform,
        placement: preferredPosition,
        position: "absolute",
      });
    };

    const updateLayout = () => {
      if (!step) return;

      // Intro / center step
      if (!step.selectors || step.position === "center") {
        centerTooltip();
        return;
      }

      let minTop = Infinity;
      let minLeft = Infinity;
      let maxBottom = -Infinity;
      let maxRight = -Infinity;
      let found = false;

      const scrollY = window.scrollY || window.pageYOffset || 0;
      const scrollX = window.scrollX || window.pageXOffset || 0;

      step.selectors.forEach((sel) => {
        const elements = document.querySelectorAll(sel);
        elements.forEach((el) => {
          const rect = el.getBoundingClientRect();
          if (rect.width > 0 && rect.height > 0) {
            found = true;
            minTop = Math.min(minTop, rect.top);
            minLeft = Math.min(minLeft, rect.left);
            maxBottom = Math.max(maxBottom, rect.bottom);
            maxRight = Math.max(maxRight, rect.right);
          }
        });
      });

      if (found) {
        const finalRect = {
          top: minTop + scrollY,
          left: minLeft + scrollX,
          width: maxRight - minLeft,
          height: maxBottom - minTop,
        };
        setHighlightRect(finalRect);
        calculateTooltipPos(finalRect, step.position);
      } else {
        // Fallback: no element yet -> center tooltip
        centerTooltip();
      }
    };

    updateLayout();
    const t = setTimeout(updateLayout, 300);

    window.addEventListener("resize", updateLayout);
    window.addEventListener("scroll", updateLayout);

    const observer = new MutationObserver(updateLayout);
    observer.observe(document.body, { childList: true, subtree: true });

    return () => {
      clearTimeout(t);
      window.removeEventListener("resize", updateLayout);
      window.removeEventListener("scroll", updateLayout);
      observer.disconnect();
    };
  }, [currentStep, steps.length]);

  // --- AUTO-SCROLL FOR SOME STEPS (like original scrollyIds) ---
  useEffect(() => {
    if (typeof document === "undefined") return;
    const step = steps[currentStep];
    if (!step || !step.autoScroll || !step.selectors || !step.selectors.length)
      return;

    const timer = window.setTimeout(() => {
      const el = document.querySelector(step.selectors[0]);
      if (!el) return;
      try {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      } catch {
        el.scrollIntoView();
      }
    }, 80);

    return () => window.clearTimeout(timer);
  }, [currentStep]);

  // --- AUTO-ADVANCE ON CLICKS (same semantics as original) ---
  useEffect(() => {
    if (typeof document === "undefined") return;
    const step = steps[currentStep];
    if (!step) return;

    const cleanups = [];

    const attach = (selector, event, handler) => {
      const els = document.querySelectorAll(selector);
      if (!els.length) return;
      els.forEach((el) => {
        el.addEventListener(event, handler);
        cleanups.push(() => el.removeEventListener(event, handler));
      });
    };

    const advance = () =>
      setCurrentStep((s) => Math.min(s + 1, steps.length - 1));

    if (step.id === "settings-tab") {
      attach("[data-tour='tab-settings']", "click", advance);
    }

    if (step.id === "editor-tab") {
      attach("[data-tour='tab-editor']", "click", advance);
    }

    if (step.id === "when-link") {
      attach(".sb-when-link", "click", advance);
    }

    if (step.id === "schedule-pop") {
      attach("[data-tour='schedule-save']", "click", advance);
    }

    if (step.id === "open-step") {
      attach(".psl-row-toggle", "click", advance);
    }

    if (step.id === "save-sequence") {
      attach("[data-tour='save-sequence']", "click", advance);
    }

    if (step.id === "contacts-tab") {
      attach("[data-tour='tab-contacts']", "click", advance);
    }

    if (step.id === "add-step") {
      attach("[data-tour='add-step']", "click", advance);
    }

    if (step.id === "search-revealed") {
      const completeFromSearch = () => {
        handleClose(true);
      };

      attach(
        "a.nav-link[href='/search'], .mobile-links a[href='/search']",
        "click",
        completeFromSearch
      );
    }

    return () => {
      cleanups.forEach((fn) => fn());
    };
  }, [currentStep, handleClose, steps.length]);

  // --- RENDER ---
  const step = steps[currentStep];
  if (!step) return null;

  const canProceed = step.validate ? step.validate() : true;

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep((s) => s + 1);
    } else {
      handleClose(true);
    }
  };

  const handleSkip = () => handleClose(true);

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep((s) => s - 1);
    }
  };

  return (
    <div
      style={{
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%",
        height: "0px",
        zIndex: 9999,
      }}
    >
      {step.isIntro && <div className="psl-tutorial-backdrop" />}

      {highlightRect && (
        <div
          className="psl-tutorial-highlight active"
          style={{
            top: highlightRect.top - 6,
            left: highlightRect.left - 6,
            width: highlightRect.width + 12,
            height: highlightRect.height + 12,
          }}
        />
      )}

      <div
        className="psl-tutorial-tooltip"
        style={{
          position: tooltipPos.position || "absolute",
          top: tooltipPos.top,
          left: tooltipPos.left,
          transform: tooltipPos.transform,
        }}
        data-placement={tooltipPos.placement}
      >
        <div className="psl-tutorial-content">
          <div className="psl-tutorial-header">
            <div className="psl-tutorial-stepnum">{currentStep + 1}</div>
            <h3>{step.title}</h3>
          </div>

          <p>{step.description}</p>

          {!canProceed && step.hint && (
            <div className="psl-tutorial-hint warning">{step.hint}</div>
          )}

          <div className="psl-tutorial-controls">
            <button
              className="psl-btn"
              onClick={handleSkip}
              style={{
                marginRight: "auto",
                color: "#6b7280",
                border: "none",
                paddingLeft: 0,
              }}
            >
              Skip
            </button>
            <button
              className="psl-btn"
              onClick={handlePrev}
              disabled={currentStep === 0}
            >
              Prev
            </button>
            <button
              className="psl-btn primary"
              onClick={handleNext}
              disabled={!canProceed}
            >
              {currentStep === steps.length - 1 ? "Finish" : "Next"}
            </button>
          </div>
        </div>

        {!step.isIntro && <div className="psl-tutorial-arrow" />}
      </div>
    </div>
  );
}

export default CampaignStepsTutorial;
